import { Link } from 'react-router-dom';
import heroPhoto from '../../assets/images_of_victoria/IMG_2280.jpg';
import detailPhoto from '../../assets/images_of_victoria/IMG_2264 (1).jpg';
import supportPhoto from '../../assets/bio/Melanie-Ray-Photo.jpg';

const summaryColumns = [
  [
    "Victoria's Place is a 501(c)(3) nonprofit organization founded by Dr. Melanie Ray, whose daughter Victoria was born with Down Syndrome. As Melanie prepared for Victoria's arrival, she reached out for guidance, support, and practical resources that would help her become the best mother and teacher she could be.",
    'After Victoria was born, Family Connections connected Melanie with a family support group where she met other mothers raising children with Down Syndrome. Those relationships gave her comfort, insight, and community at a time when support mattered most.',
    'With the help of an occupational therapist, physical therapist, and speech early interventionist, Victoria made significant improvements in her developmental skills. Melanie saw early on that her daughter was loving, joyful, and capable of remarkable growth with the right support around her.',
  ],
  [
    'Melanie also brought professional experience to the journey. She has worked as a mental health counselor, a behavior therapist for children with autism, and a program director developing services for people with disabilities. Through Buddy Walk participation and advocacy for her daughter and other children, she built lasting friendships and continued serving other parents along the way.',
    "Working tirelessly with Victoria made one thing clear: more children needed a place to connect, create, and grow together. Victoria's Place was shaped by that conviction and by the values Melanie saw matter most: unshakable faith in God, strong parental involvement, community resources, a strong church family, and family support.",
    "Today, Victoria's Place is designed as an interactive recreational place for children ages 3 to 18 with Down Syndrome. It creates room for art, music, sports, fine motor skill activities, reading, and conversations that build self-esteem, social development, confidence, healthy choices, and lasting friendships.",
  ],
];

const foundationValues = [
  'Unshakable faith in God',
  'Strong parental involvement',
  'Community resources',
  'A strong church family',
  'Family support',
];

const principles = [
  {
    number: '01',
    title: 'Mission statement',
    description:
      'To provide a loving, compassionate, and safe environment for children with Down Syndrome that will foster their social interactions, enhance their self-esteem, and build character.',
  },
  {
    number: '02',
    title: 'Vision statement',
    description: 'Building the best world possible for children with Down Syndrome.',
  },
  {
    number: '03',
    title: 'Statement of needs',
    description:
      "Victoria's Place operates for children ages 3 to 18 with Down Syndrome and is rooted in the belief that every child deserves peers, friendship, and a place to interact in a loving and compassionate environment. The need is not only for activities, but for belonging, connection, and a trusted place families can count on.",
  },
];

export default function WhoWeAre() {
  return (
    <div className="page-shell about-page space-y-8">
      <section className="about-hero">
        <div className="about-hero-copy">
          <span className="eyebrow">Let&apos;s build together</span>
          <p className="about-kicker">Who we are</p>
          <h1>Let&apos;s create a better world for children and families.</h1>
          <p className="about-hero-lead">
            We are parents, we are a community, and we are family. Victoria&apos;s Place exists to serve children with
            Down Syndrome through programs that create room for expression, connection, and steady encouragement.
          </p>
          <p className="about-hero-support">
            What began as one mother&apos;s commitment to her daughter grew into a nonprofit rooted in faith, support,
            and the belief that children deserve every opportunity to grow with dignity and joy.
          </p>

          <div className="about-hero-actions">
            <a href="#our-approach" className="button-secondary tap-target">
              Read our approach
            </a>
            <Link to="/board-members" className="button-secondary tap-target">
              Meet the board
            </Link>
            <Link to="/give" className="button-primary tap-target">
              Donate
            </Link>
          </div>

          <div className="about-hero-meta">
            <div className="about-meta-item">
              <span>Founded by</span>
              <strong>Dr. Melanie Ray</strong>
            </div>
            <div className="about-meta-item">
              <span>Organization</span>
              <strong>501(c)(3) nonprofit</strong>
            </div>
            <div className="about-meta-item">
              <span>Focused on</span>
              <strong>Children ages 3 to 18</strong>
            </div>
          </div>
        </div>

        <div className="about-hero-media">
          <figure className="about-photo-frame about-photo-frame--hero">
            <img src={heroPhoto} alt="Victoria standing outside in a bright pink dress" />
          </figure>
          <div className="about-hero-note">
            <p className="info-kicker">Our motto</p>
            <p>
              You can do anything and be anything you want to be. No worry no hurry at Victoria&apos;s
              Place.
            </p>
          </div>
        </div>
      </section>

      <section className="about-intro-section">
        <article className="about-intro-copy">
          <p className="home-section-label">Who we are</p>
          <h2>We give children positive outlets to express, connect, and belong.</h2>
          <p>
            Victoria&apos;s Place is here to help serve children with Down Syndrome everywhere. Our goal is to help
            make this world a better place for children with Down Syndrome through the programs we offer.
          </p>
          <p>
            We believe in giving children with Down Syndrome a chance to express themselves by giving them a positive
            outlet through activities such as art, music, sports, help with motor skills, and reading.
          </p>
          <blockquote className="about-quote">
            <span className="quote-mark">&ldquo;</span>
            <p>At Victoria&apos;s Place, kids can have fun, learn, grow, and interact with their peers.</p>
          </blockquote>
        </article>

        <div className="about-photo-stack">
          <figure className="about-photo-frame about-photo-frame--portrait">
            <img src={detailPhoto} alt="Victoria sitting outside in fairy wings and flowers" />
          </figure>
          <figure className="about-photo-frame about-photo-frame--support">
            <img src={supportPhoto} alt="Dr. Melanie Ray holding Victoria" />
          </figure>
        </div>
      </section>

      <section id="our-approach" className="about-summary-section">
        <div className="about-summary-heading">
          <div>
            <p className="home-section-label">Our approach</p>
            <h2>Executive summary</h2>
          </div>
          <p>
            Victoria&apos;s Place was shaped by lived experience, practical support, and a determination to create a
            place where children with Down Syndrome can grow in a loving environment with people who care.
          </p>
        </div>

        <div className="about-summary-grid">
          {summaryColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="about-summary-column">
              {column.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          ))}
        </div>

        <aside className="about-foundation-strip">
          <div className="about-foundation-copy">
            <p className="info-kicker">What shaped the vision</p>
            <h3>Faith, family, and support are built into the foundation.</h3>
          </div>
          <div className="about-foundation-values">
            {foundationValues.map((value) => (
              <span key={value}>{value}</span>
            ))}
          </div>
        </aside>
      </section>

      <section className="about-principles">
        {principles.map((principle) => (
          <article key={principle.number} className="about-principle">
            <div className="about-principle-number">{principle.number}</div>
            <div>
              <h2>{principle.title}</h2>
              <p>{principle.description}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="about-cta">
        <div className="about-cta-copy">
          <p className="home-section-label home-section-label--light">Donate</p>
          <h2>Support us and change the course of a child&apos;s life today.</h2>
          <p>
            Your generosity helps Victoria&apos;s Place stay welcoming, faith-rooted, and ready to serve children and
            families who need connection, encouragement, and care.
          </p>
        </div>
        <div className="about-cta-actions">
          <Link to="/give" className="button-primary tap-target">
            Donate
          </Link>
          <Link to="/board-members" className="button-secondary tap-target">
            Meet the board
          </Link>
        </div>
      </section>
    </div>
  );
}
